import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Gavel } from '@mui/icons-material';
import api from '../services/api';

const BidForm = ({ auction, onBidPlaced }) => {
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const minBid = parseFloat(auction.current_bid || auction.start_price) + parseFloat(auction.bid_increment);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(bidAmount);

    if (isNaN(amount) || amount < minBid) {
      setError(`Minimum bid is $${minBid.toFixed(2)}`);
      return;
    }

    setLoading(true);

    try {
      const response = await api.post(`/auctions/${auction.id}/bid`, {
        bid_amount: amount,
      });

      setBidAmount('');
      if (onBidPlaced) {
        onBidPlaced(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place bid');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickBid = (type) => {
    if (type === 'min') {
      setBidAmount(minBid.toFixed(2));
    } else if (type === 'plus5') {
      setBidAmount((minBid + 5).toFixed(2));
    } else if (type === 'plus10') {
      setBidAmount((minBid + 10).toFixed(2));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Place Your Bid
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Current bid: ${parseFloat(auction.current_bid || auction.start_price).toFixed(2)}
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Minimum bid: ${minBid.toFixed(2)}
      </Typography>

      <TextField
        fullWidth
        label="Bid Amount"
        type="number"
        value={bidAmount}
        onChange={(e) => setBidAmount(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        inputProps={{
          step: '0.01',
          min: minBid,
        }}
        sx={{ my: 2 }}
      />

      <Box display="flex" gap={1} mb={2}>
        <Button size="small" variant="outlined" onClick={() => handleQuickBid('min')}>
          Min Bid
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleQuickBid('plus5')}>
          +$5
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleQuickBid('plus10')}>
          +$10
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        startIcon={loading ? <CircularProgress size={20} /> : <Gavel />}
        disabled={loading || !bidAmount}
      >
        {loading ? 'Placing Bid...' : 'Place Bid'}
      </Button>
    </Box>
  );
};

export default BidForm;
